import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BarChart3, Wallet, FileText, ShieldCheck, Award, Calendar, Download, HelpCircle, Settings, Plus, LogOut, ChevronLeft, ChevronRight, Copy, Key, TrendingUp, TrendingDown, DollarSign, Target, AlertCircle, LayoutGrid, Clock, Bell, User, Save, CheckCircle2, PenTool, Gift, Crown, Lock, Sparkles, Upload, Shield } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';
import { fbTrack } from '../utils/pixel';

const SIDEBAR_ITEMS = [
  { id: 'overview', label: 'Account Overview', icon: BarChart3 },
  { id: 'payouts', label: 'Payouts', icon: Wallet },
  { id: 'elite-salary', label: 'Elite Salaries', icon: Crown, gold: true },
  { id: 'kyc', label: 'KYC Verification', icon: ShieldCheck },
  { id: 'affiliate', label: 'Affiliate Program', icon: Gift },
  { id: 'billing', label: 'Billing', icon: FileText },
  { id: 'contracts', label: 'Contracts', icon: ShieldCheck },
  { id: 'certificates', label: 'Certificates', icon: Award },
  { id: 'calendar', label: 'Economic Calendar', icon: Calendar },
  { id: 'downloads', label: 'Downloads', icon: Download },
  { id: 'help', label: 'Help Center', icon: HelpCircle },
  { id: 'settings', label: 'Settings', icon: Settings },
];

const Sidebar = ({ active, setActive, collapsed, setCollapsed, onLogout, mobileOpen, setMobileOpen }) => (
  <>
    {/* Mobile overlay */}
    {mobileOpen && <div className="fixed inset-0 bg-black/60 z-40 md:hidden" onClick={() => setMobileOpen(false)} />}
    <aside className={`fixed left-0 top-0 bottom-0 z-50 bg-[#0A0F1C] border-r border-white/5 flex flex-col transition-all duration-300
      ${collapsed ? 'w-16' : 'w-60'}
      ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
      data-testid="dashboard-sidebar">
      <div className="p-3 flex items-center justify-between border-b border-white/5 h-16">
        {!collapsed && <img src="/ayronn-logo.png" alt="AYRONN" className="h-[60px] md:h-[77px] w-auto" />}
        <button onClick={() => { setCollapsed(!collapsed); if(window.innerWidth<768) setMobileOpen(false); }} className="text-zinc-500 hover:text-white transition-colors" data-testid="sidebar-toggle">
          {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
        </button>
      </div>
    <nav className="flex-1 py-4 overflow-y-auto no-scrollbar">
      {SIDEBAR_ITEMS.map(item => {
        const Icon = item.icon;
        const isGold = item.gold;
        const isActive = active === item.id;
        return (
          <button key={item.id} onClick={() => setActive(item.id)} data-testid={`sidebar-${item.id}`}
            className={`w-full flex items-center gap-3 px-4 py-3 text-sm transition-all ${
              isActive
                ? (isGold ? 'bg-brand-gold/10 text-brand-gold border-r-2 border-brand-gold' : 'bg-brand-primary/10 text-white border-r-2 border-brand-primary')
                : (isGold ? 'text-brand-gold/80 hover:text-brand-gold hover:bg-brand-gold/[0.04]' : 'text-zinc-500 hover:text-white hover:bg-white/[0.03]')
            }`}>
            <Icon className="w-5 h-5 flex-shrink-0" />
            {!collapsed && <span className="truncate flex items-center gap-1.5">{item.label}{isGold && <Sparkles className="w-3 h-3 opacity-70" />}</span>}
          </button>
        );
      })}
    </nav>
    <div className="p-4 border-t border-white/5 space-y-3">
      <Link to="/pricing" className={`flex items-center justify-center gap-2 py-2.5 bg-brand-primary text-black font-semibold rounded-xl hover:glow-primary transition-all text-sm ${collapsed ? 'px-2' : 'px-4'}`} data-testid="start-new-challenge-btn">
        <Plus className="w-4 h-4" />{!collapsed && 'Start New Challenge'}
      </Link>
      {!collapsed && <button onClick={onLogout} className="w-full flex items-center gap-2 px-4 py-2 text-zinc-500 hover:text-white text-sm" data-testid="sidebar-logout"><LogOut className="w-4 h-4" /> Logout</button>}
    </div>
  </aside>
  </>
);

const ELITE_SALARY_BY_SIZE = { 5000: 250, 10000: 500, 25000: 1000, 50000: 1800, 100000: 2500, 200000: 5000 };
const ELITE_PAYOUTS_REQUIRED = 12;

const AccountCard = ({ account, onMetrics, onCopyCredentials, eliteProgress }) => {
  const sc = { active: 'bg-brand-positive/20 text-brand-positive', passed: 'bg-brand-primary/20 text-brand-primary', failed: 'bg-brand-negative/20 text-brand-negative', breached: 'bg-red-500/20 text-red-400', inactive: 'bg-yellow-400/20 text-yellow-400', archived: 'bg-zinc-600/20 text-zinc-400', pending: 'bg-yellow-400/20 text-yellow-400', funded: 'bg-brand-positive/20 text-brand-positive' };
  const pp = account.profit_target > 0 ? Math.min((account.profit_loss / account.profit_target) * 100, 100) : 0;
  const [showPwd, setShowPwd] = useState(false);
  const copyField = (label, value) => {
    if (!value) return;
    navigator.clipboard.writeText(value).then(() => toast.success(`${label} copied!`)).catch(() => toast.error('Failed'));
  };
  const mask = (s) => (s ? '•'.repeat(Math.min(12, s.length)) : '');

  // Elite Salary potential — show on funded/active accounts only
  const salary = ELITE_SALARY_BY_SIZE[account.account_size] || 0;
  const fundedLike = ['active', 'funded', 'passed'].includes(account.status);
  const payoutsDone = eliteProgress?.payouts_completed ?? 0;
  const eliteUnlocked = payoutsDone >= ELITE_PAYOUTS_REQUIRED;
  return (
    <div className="bg-[#0F1420] border border-white/5 rounded-2xl p-6 hover:border-white/10 transition-all card-shine" data-testid={`account-card-${account.account_number}`}>
      <div className="flex items-start justify-between mb-4">
        <h3 className="text-2xl font-bold font-mono text-white">{account.account_number}</h3>
      </div>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold uppercase ${sc[account.status] || 'bg-zinc-700 text-zinc-400'}`}>{account.status}</span>
        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-white/5 text-zinc-300 flex items-center gap-1.5">
          <img src="/exness-logo.png" alt="Exness" className="h-3 w-auto" /> {account.platform || 'Exness MT5'}
        </span>
      </div>
      {account.status === 'breached' && account.breach_reason && (
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
          <p className="text-red-400 text-xs font-medium">BREACHED: {account.breach_reason}</p>
        </div>
      )}
      {account.status === 'inactive' && (
        <div className="mb-4 p-3 bg-yellow-400/10 border border-yellow-400/30 rounded-xl">
          <p className="text-yellow-400 text-xs font-medium">Account inactive — no trading activity detected</p>
        </div>
      )}
      {!account.consistency_met && account.status !== 'breached' && (
        <div className="mb-4 p-3 bg-yellow-400/10 border border-yellow-400/30 rounded-xl">
          <p className="text-yellow-400 text-xs font-medium">Consistency criteria not met — payouts blocked</p>
        </div>
      )}

      {/* Credentials Section */}
      {!account.credentials_assigned ? (
        <div className="mb-4 p-4 bg-yellow-400/5 border border-yellow-400/30 rounded-xl flex items-start gap-3" data-testid="credentials-pending">
          <Clock className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <div className="text-yellow-400 text-xs font-bold uppercase tracking-wider mb-0.5">Pending Credentials</div>
            <div className="text-zinc-400 text-xs">Your trading credentials will appear here shortly. You'll be notified once assigned.</div>
          </div>
        </div>
      ) : (
        <div className="mb-4 p-4 bg-brand-primary/5 border border-brand-primary/30 rounded-xl space-y-2" data-testid="credentials-section">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] text-brand-primary font-bold uppercase tracking-wider flex items-center gap-1.5"><Key className="w-3 h-3" /> Trading Credentials</div>
            <button onClick={() => onCopyCredentials(account)} className="text-[10px] px-2 py-0.5 bg-white/5 border border-white/10 text-zinc-300 rounded hover:bg-brand-primary hover:text-black font-semibold" data-testid="copy-all-creds-btn">Copy All</button>
          </div>
          <div className="grid grid-cols-1 gap-1.5 text-xs">
            <div className="flex items-center justify-between gap-2">
              <span className="text-zinc-500">Login ID</span>
              <div className="flex items-center gap-1.5">
                <span className="text-white font-mono" data-testid="cred-login-id">{account.login_id || '—'}</span>
                <button onClick={() => copyField('Login ID', account.login_id)} className="p-1 hover:bg-white/10 rounded"><Copy className="w-3 h-3 text-zinc-500" /></button>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-zinc-500">Password</span>
              <div className="flex items-center gap-1.5">
                <span className="text-white font-mono" data-testid="cred-password">{showPwd ? account.credentials_password : mask(account.credentials_password)}</span>
                <button onClick={() => setShowPwd(v => !v)} className="p-1 hover:bg-white/10 rounded text-zinc-500 hover:text-white text-[10px] font-bold" data-testid="toggle-password-btn">{showPwd ? 'HIDE' : 'SHOW'}</button>
                <button onClick={() => copyField('Password', account.credentials_password)} className="p-1 hover:bg-white/10 rounded"><Copy className="w-3 h-3 text-zinc-500" /></button>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-zinc-500">Server</span>
              <div className="flex items-center gap-1.5">
                <span className="text-white font-mono" data-testid="cred-server">{account.server || '—'}</span>
                <button onClick={() => copyField('Server', account.server)} className="p-1 hover:bg-white/10 rounded"><Copy className="w-3 h-3 text-zinc-500" /></button>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-zinc-500">Platform</span>
              <span className="text-white font-mono">{account.platform || 'Exness MT5'}</span>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4 mb-4">
        <div className="text-xs text-zinc-500 uppercase tracking-wider mb-1">Program</div>
        <div className="text-lg font-semibold text-white">{account.plan_name || `${account.challenge_type} Challenge`}</div>
        <div className="flex items-center gap-2 mt-2 text-xs text-zinc-500"><Calendar className="w-3 h-3" /> Start {new Date(account.created_at).toLocaleDateString('en-GB')}</div>
      </div>

      {/* Elite Salary potential (shown on funded-like accounts) */}
      {fundedLike && salary > 0 && (
        <div className={`mb-4 p-4 rounded-xl border ${eliteUnlocked ? 'bg-gradient-to-br from-brand-gold/15 to-transparent border-brand-gold/40 shadow-[0_0_30px_rgba(212,175,55,0.18)]' : 'bg-brand-gold/[0.04] border-brand-gold/15'}`} data-testid={`elite-salary-${account.account_number}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-2.5 min-w-0">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${eliteUnlocked ? 'bg-brand-gold/25 border border-brand-gold/50' : 'bg-brand-gold/10 border border-brand-gold/20'}`}>
                <Crown className="w-4 h-4 text-brand-gold" strokeWidth={1.6} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] uppercase tracking-wider text-brand-gold font-black">Elite Salary</span>
                  {!eliteUnlocked && <Lock className="w-3 h-3 text-brand-gold/50" />}
                </div>
                <div className="text-brand-gold font-black text-xl font-mono leading-tight mt-0.5">
                  ${salary.toLocaleString()}<span className="text-[10px] text-brand-gold/70 font-bold">/mo</span>
                </div>
                <div className="text-[10px] text-zinc-500 mt-0.5">
                  {eliteUnlocked
                    ? <span className="text-brand-gold/90 font-semibold">UNLOCKED — visit Elite Salaries tab to claim</span>
                    : `${payoutsDone} / ${ELITE_PAYOUTS_REQUIRED} consistent payouts on this account`}
                </div>
              </div>
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-3 h-1.5 bg-black/40 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${eliteUnlocked ? 'bg-gradient-to-r from-brand-gold to-brand-gold-soft' : 'bg-brand-gold/60'}`}
              style={{ width: `${Math.min(100, (payoutsDone / ELITE_PAYOUTS_REQUIRED) * 100)}%` }} />
          </div>
        </div>
      )}

      <div className="mb-4">
        <div className="flex justify-between text-sm mb-1"><span className="text-zinc-500">Balance</span><span className="text-white font-bold font-mono">${Number(account.balance).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span></div>
        <div className="w-full h-1.5 bg-zinc-800 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-brand-primary to-brand-secondary rounded-full" style={{width:`${Math.min(Math.max(pp,5),100)}%`}} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
        <div><span className="text-zinc-500 text-xs block">Equity</span><span className="text-white font-semibold font-mono">${Number(account.equity).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span></div>
        <div><span className="text-zinc-500 text-xs block">P&L</span><span className={`font-semibold font-mono ${account.profit_loss>=0?'text-brand-positive':'text-brand-negative'}`}>{account.profit_loss>=0?'+':''}${Number(account.profit_loss).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</span></div>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={() => onMetrics(account)} className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-brand-primary/10 border border-brand-primary/30 text-brand-primary font-semibold rounded-lg hover:bg-brand-primary/20 transition-all text-sm" data-testid="go-to-metrics-btn"><LayoutGrid className="w-4 h-4" /> Go to metrics</button>
      </div>
    </div>
  );
};

const MetricsView = ({ account, onBack }) => {
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    api.get(`/accounts/${account.id}/history`).then(r => setHistory(r.data)).catch(() => {}).finally(() => setLoadingHistory(false));
  }, [account.id]);

  // ONLY use real historical data - NO fake/generated data
  const chartData = useMemo(() => {
    if (history.length > 0) return history;
    return []; // Empty = show empty state
  }, [history]);

  const pp = account.profit_target > 0 ? (account.profit_loss / account.profit_target) * 100 : 0;
  const dd = account.max_drawdown > 0 ? (Math.max(0, account.account_size - Math.min(account.balance, account.equity)) / account.max_drawdown) * 100 : 0;
  const riskColor = account.risk_status === 'safe' ? 'text-brand-positive' : account.risk_status === 'warning' ? 'text-yellow-400' : 'text-red-400';

  return (
    <div>
      <button onClick={onBack} className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 text-sm" data-testid="back-to-overview"><ChevronLeft className="w-4 h-4" /> Back</button>
      <div className="flex items-center gap-4 mb-6 flex-wrap">
        <h2 className="text-2xl font-bold text-white font-mono">{account.account_number}</h2>
        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${account.status==='active'?'bg-brand-positive/20 text-brand-positive':account.status==='passed'||account.status==='phase1_passed'?'bg-brand-primary/20 text-brand-primary':account.status==='funded'?'bg-emerald-500/20 text-emerald-400':account.status==='breached'?'bg-red-500/20 text-red-400':account.status==='inactive'?'bg-yellow-400/20 text-yellow-400':account.status==='archived'?'bg-zinc-600/20 text-zinc-400':'bg-brand-negative/20 text-brand-negative'}`}>{account.status === 'phase1_passed' ? 'Phase 1 Passed' : account.status}</span>
        {account.phase_label && <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-brand-primary/10 text-brand-primary">{account.phase_label}</span>}
        <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-white/5 text-zinc-300 flex items-center gap-1.5"><img src="/exness-logo.png" alt="Exness" className="h-3 w-auto" /> Exness MT5</span>
        <span className="text-xs text-zinc-500">Simulated environment</span>
      </div>

      {account.status === 'breached' && account.breach_reason && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl"><p className="text-red-400 text-sm font-medium">BREACHED: {account.breach_reason}</p></div>
      )}

      {/* Key Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-8">
        {[
          {l:'Balance',v:`$${Number(account.balance).toLocaleString(undefined,{minimumFractionDigits:2})}`,c:'text-white'},
          {l:'Equity',v:`$${Number(account.equity).toLocaleString(undefined,{minimumFractionDigits:2})}`,c:'text-white'},
          {l:'Profit/Loss',v:`${account.profit_loss>=0?'+':''}$${Number(account.profit_loss).toLocaleString(undefined,{minimumFractionDigits:2})}`,c:account.profit_loss>=0?'text-brand-positive':'text-brand-negative'},
          {l:'DD Remaining',v:`$${Number(account.remaining_drawdown||0).toLocaleString(undefined,{minimumFractionDigits:2})}`,c:riskColor},
          {l:'Daily Loss Left',v:`$${Number(account.daily_loss_remaining||0).toLocaleString(undefined,{minimumFractionDigits:2})}`,c:'text-white'},
          {l:'Risk Status',v:(account.risk_status||'safe').toUpperCase(),c:riskColor},
        ].map((s,i)=>(
          <div key={i} className="p-4 bg-[#0F1420] border border-white/5 rounded-xl"><div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">{s.l}</div><div className={`text-lg font-bold font-mono ${s.c}`}>{s.v}</div></div>
        ))}
      </div>

      {/* Equity Curve Chart */}
      <div className="bg-[#0F1420] border border-white/5 rounded-2xl p-6 mb-6" data-testid="metrics-equity-chart">
        <h3 className="text-lg font-bold text-white mb-4">Equity Curve</h3>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={chartData}><defs><linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00D4FF" stopOpacity={0.25}/><stop offset="100%" stopColor="#00D4FF" stopOpacity={0}/></linearGradient></defs>
              <XAxis dataKey="date" stroke="rgba(255,255,255,0.05)" tick={{fill:'#71717A',fontSize:10}} interval="preserveStartEnd"/>
              <YAxis stroke="rgba(255,255,255,0.05)" tick={{fill:'#71717A',fontSize:10}} tickFormatter={v=>`$${(v/1000).toFixed(1)}k`}/>
              <Tooltip content={({active,payload,label})=>active&&payload?.length?<div className="bg-[#0F1420] border border-white/10 rounded-lg p-3 text-xs"><div className="text-zinc-400 mb-1">{label}</div><div className="text-brand-primary font-mono font-bold">${Number(payload[0].value).toLocaleString()}</div></div>:null}/>
              <Area type="monotone" dataKey="equity" stroke="#00D4FF" strokeWidth={2} fill="url(#eqGrad)"/></AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex items-center justify-center h-[200px] text-zinc-600 text-sm" data-testid="no-chart-data">No trading data available</div>
        )}
      </div>

      {/* Daily PnL Bar Chart + Drawdown Chart */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-[#0F1420] border border-white/5 rounded-2xl p-6" data-testid="daily-pnl-chart">
          <h3 className="text-lg font-bold text-white mb-4">Daily P&L</h3>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData}>
                <XAxis dataKey="date" stroke="rgba(255,255,255,0.05)" tick={{fill:'#71717A',fontSize:9}} interval="preserveStartEnd"/>
                <YAxis stroke="rgba(255,255,255,0.05)" tick={{fill:'#71717A',fontSize:9}} tickFormatter={v=>`$${v}`}/>
                <Tooltip content={({active,payload,label})=>active&&payload?.length?<div className="bg-[#0F1420] border border-white/10 rounded-lg p-2 text-xs"><div className="text-zinc-400 mb-1">{label}</div><div className={`font-mono font-bold ${payload[0].value>=0?'text-brand-positive':'text-brand-negative'}`}>${Number(payload[0].value).toLocaleString()}</div></div>:null}/>
                <Bar dataKey="daily_pnl" radius={[3,3,0,0]} fill="#00D4FF">
                  {chartData.map((entry, index) => (
                    <Cell key={index} fill={entry.daily_pnl >= 0 ? '#00FF94' : '#FF3366'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[150px] text-zinc-600 text-sm">No trading data available</div>
          )}
        </div>
        <div className="bg-[#0F1420] border border-white/5 rounded-2xl p-6" data-testid="drawdown-chart">
          <h3 className="text-lg font-bold text-white mb-4">Drawdown</h3>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={chartData}><defs><linearGradient id="ddGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FF3366" stopOpacity={0.3}/><stop offset="100%" stopColor="#FF3366" stopOpacity={0}/></linearGradient></defs>
                <XAxis dataKey="date" stroke="rgba(255,255,255,0.05)" tick={{fill:'#71717A',fontSize:9}} interval="preserveStartEnd"/>
                <YAxis stroke="rgba(255,255,255,0.05)" tick={{fill:'#71717A',fontSize:9}} tickFormatter={v=>`$${v}`}/>
                <Tooltip content={({active,payload,label})=>active&&payload?.length?<div className="bg-[#0F1420] border border-white/10 rounded-lg p-2 text-xs"><div className="text-zinc-400 mb-1">{label}</div><div className="text-red-400 font-mono font-bold">${Number(payload[0].value).toLocaleString()}</div></div>:null}/>
                <Area type="monotone" dataKey="drawdown" stroke="#FF3366" strokeWidth={2} fill="url(#ddGrad)"/>
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[150px] text-zinc-600 text-sm">No trading data available</div>
          )}
        </div>
      </div>

      {/* Progress Bars */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {account.profit_target > 0 && (
          <div className="bg-[#0F1420] border border-white/5 rounded-2xl p-6">
            <div className="flex justify-between mb-2"><span className="text-sm text-zinc-400">Profit Target</span><span className="text-sm font-mono text-brand-positive">{Math.min(pp,100).toFixed(1)}%</span></div>
            <div className="w-full h-3 bg-zinc-800/50 rounded-full overflow-hidden"><motion.div initial={{width:0}} animate={{width:`${Math.min(Math.max(pp,0),100)}%`}} transition={{duration:1}} className="h-full rounded-full bg-gradient-to-r from-brand-positive to-brand-positive/60"/></div>
            <div className="flex justify-between mt-2 text-xs text-zinc-500"><span>${Number(account.profit_loss).toLocaleString()} earned</span><span>${Number(account.profit_target).toLocaleString()} target</span></div>
          </div>
        )}
        <div className="bg-[#0F1420] border border-white/5 rounded-2xl p-6">
          <div className="flex justify-between mb-2"><span className="text-sm text-zinc-400">Drawdown Used</span><span className={`text-sm font-mono ${dd<50?'text-brand-positive':dd<80?'text-yellow-400':'text-red-400'}`}>{Math.min(dd,100).toFixed(1)}%</span></div>
          <div className="w-full h-3 bg-zinc-800/50 rounded-full overflow-hidden"><motion.div initial={{width:0}} animate={{width:`${Math.min(dd,100)}%`}} transition={{duration:1}} className={`h-full rounded-full bg-gradient-to-r ${dd<50?'from-brand-positive to-brand-positive/60':dd<80?'from-yellow-400 to-yellow-400/60':'from-red-400 to-red-400/60'}`}/></div>
          <div className="flex justify-between mt-2 text-xs text-zinc-500"><span>Max DD: ${Number(account.max_drawdown).toLocaleString()}</span><span>Remaining: ${Number(account.remaining_drawdown||0).toLocaleString()}</span></div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="bg-[#0F1420] border border-white/5 rounded-2xl p-6">
        <h3 className="text-lg font-bold text-white mb-4">Trading Stats</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div><span className="text-xs text-zinc-500 block">Daily P&L</span><span className={`text-lg font-bold font-mono ${account.daily_pnl>=0?'text-brand-positive':'text-brand-negative'}`}>{account.daily_pnl>=0?'+':''}${Number(account.daily_pnl).toLocaleString()}</span></div>
          <div><span className="text-xs text-zinc-500 block">Trading Days</span><span className="text-lg font-bold text-white">{account.trading_days}/{account.min_trading_days||'--'}</span></div>
          <div><span className="text-xs text-zinc-500 block">Profit Split</span><span className="text-lg font-bold text-white">{account.profit_split}%</span></div>
          <div><span className="text-xs text-zinc-500 block">Payout</span><span className="text-lg font-bold text-brand-primary">Daily</span></div>
          <div><span className="text-xs text-zinc-500 block">Equity Change</span><span className={`text-lg font-bold font-mono ${(account.equity_change_pct||0)>=0?'text-brand-positive':'text-brand-negative'}`}>{(account.equity_change_pct||0)>=0?'+':''}{(account.equity_change_pct||0).toFixed(2)}%</span></div>
          <div><span className="text-xs text-zinc-500 block">Trades</span><span className="text-lg font-bold text-white">{account.num_trades||0}</span></div>
          <div><span className="text-xs text-zinc-500 block">Win Rate</span><span className="text-lg font-bold text-white">{account.win_rate||0}%</span></div>
          <div><span className="text-xs text-zinc-500 block">Highest Equity</span><span className="text-lg font-bold text-white font-mono">${Number(account.highest_equity||account.equity).toLocaleString()}</span></div>
        </div>
      </div>
    </div>
  );
};

const EliteSalaryView = ({ eliteData, eliteLoading }) => {
  if (eliteLoading) {
    return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" /></div>;
  }
  const data = eliteData || { per_account: [], best_payouts: 0, payouts_required: 12, is_eligible: false, elite_granted: false };
  const unlocked = data.is_eligible || data.elite_granted;
  const bestSize = data.per_account.reduce((m, a) => Math.max(m, a.account_size), 0);
  const bestSalary = ELITE_SALARY_BY_SIZE[bestSize] || 0;

  return (
    <div data-testid="elite-salary-view">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-black text-white flex items-center gap-2.5">
            <Crown className="w-7 h-7 text-brand-gold" /> Elite Salaries
            {unlocked && <span className="px-2 py-0.5 text-[10px] font-black uppercase tracking-wider bg-brand-gold/20 text-brand-gold border border-brand-gold/40 rounded-full">Unlocked</span>}
          </h2>
          <p className="text-zinc-500 text-sm mt-1">
            Monthly salary rewards for traders who complete <strong className="text-brand-gold">{data.payouts_required} consistent payouts</strong> from a single funded account.
          </p>
        </div>
      </div>

      {/* LOCKED state */}
      {!unlocked && (
        <div className="relative overflow-hidden bg-gradient-to-br from-brand-gold/[0.06] to-transparent border border-brand-gold/15 rounded-2xl p-8 md:p-10" data-testid="elite-salary-locked">
          <div className="absolute -top-20 -right-20 w-72 h-72 bg-brand-gold/8 rounded-full blur-[100px] pointer-events-none" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-brand-gold/10 border border-brand-gold/30 flex items-center justify-center">
                <Lock className="w-6 h-6 text-brand-gold" strokeWidth={1.5} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-brand-gold font-black">Locked Until Eligibility</div>
                <h3 className="text-xl md:text-2xl font-black text-white">Earn your Elite Tier.</h3>
              </div>
            </div>
            <p className="text-sm text-zinc-300 max-w-2xl leading-relaxed">
              The Elite Salaries area unlocks automatically the moment one of your accounts hits <strong className="text-brand-gold">{data.payouts_required} consistent paid payouts</strong>. Keep trading, request your payouts on schedule — the system tracks everything.
            </p>

            {/* Best progress */}
            <div className="mt-6 p-5 rounded-xl bg-black/40 border border-brand-gold/15 max-w-xl">
              <div className="flex items-center justify-between text-xs uppercase tracking-wider text-zinc-400 font-bold mb-2">
                <span>Your Best Progress</span>
                <span className="text-brand-gold font-mono">{data.best_payouts} / {data.payouts_required}</span>
              </div>
              <div className="h-2 bg-black/60 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-brand-gold/60 to-brand-gold rounded-full transition-all" style={{ width: `${Math.min(100, (data.best_payouts / data.payouts_required) * 100)}%` }} />
              </div>
              <div className="text-[10px] text-zinc-500 mt-2">{Math.max(0, data.payouts_required - data.best_payouts)} more payouts to unlock</div>
            </div>

            {/* Eligibility rules */}
            <div className="mt-7 grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { icon: Crown, t: 'Single Account', d: 'All 12 payouts must come from one funded account.' },
                { icon: CheckCircle2, t: 'Good Standing', d: 'Account must not be breached or archived.' },
                { icon: Target, t: 'Consistency', d: 'Maintain consistency framework throughout.' },
              ].map((it, i) => (
                <div key={i} className="p-4 rounded-xl bg-white/[0.02] border border-white/8">
                  <it.icon className="w-4 h-4 text-brand-gold mb-2" strokeWidth={1.5} />
                  <div className="text-white text-sm font-bold">{it.t}</div>
                  <div className="text-[11px] text-zinc-400 mt-1 leading-relaxed">{it.d}</div>
                </div>
              ))}
            </div>

            <p className="mt-5 text-[10px] text-zinc-600 leading-relaxed max-w-2xl">
              The Elite Salary is a discretionary performance reward, not a guaranteed return. AYRONN Funding is an evaluation &amp; performance-reward platform. Eligibility &amp; payment subject to internal review.
            </p>
          </div>
        </div>
      )}

      {/* UNLOCKED state */}
      {unlocked && (
        <div className="space-y-6" data-testid="elite-salary-unlocked">
          <div className="relative overflow-hidden bg-gradient-to-br from-brand-gold/20 via-brand-gold/[0.06] to-transparent border-2 border-brand-gold/40 rounded-2xl p-8 shadow-[0_0_60px_rgba(212,175,55,0.18)]">
            <div className="absolute -top-20 -right-20 w-72 h-72 bg-brand-gold/15 rounded-full blur-[100px] pointer-events-none" />
            <div className="relative flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-brand-gold" />
                  <span className="text-[10px] uppercase tracking-[0.2em] text-brand-gold font-black">Elite Tier Active</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-black bg-gradient-to-r from-brand-gold-soft via-brand-gold to-brand-gold-soft bg-clip-text text-transparent">${bestSalary.toLocaleString()}<span className="text-base text-brand-gold/70 font-bold">/month</span></h3>
                <p className="text-zinc-300 text-sm mt-2 max-w-md">Congratulations — you have unlocked the Elite Salary tier for your ${bestSize.toLocaleString()} account.</p>
              </div>
              <a href="mailto:ayronnsupport@gmail.com?subject=Elite%20Salary%20Claim" data-testid="elite-claim-btn"
                className="inline-flex items-center gap-2 px-5 py-3 bg-brand-gold text-black font-black rounded-xl hover:shadow-[0_0_35px_rgba(212,175,55,0.55)] transition-all text-sm">
                Claim Salary <Crown className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Per-account progress table */}
      {data.per_account.length > 0 && (
        <div className="mt-8">
          <h3 className="text-sm font-black text-white uppercase tracking-wider mb-3 flex items-center gap-2">
            <Crown className="w-4 h-4 text-brand-gold" /> Account Progress
          </h3>
          <div className="bg-[#0F1420] border border-white/5 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-white/5">
                  <th className="text-left p-4 text-[10px] uppercase text-zinc-500 font-bold">Account</th>
                  <th className="text-left p-4 text-[10px] uppercase text-zinc-500 font-bold">Plan</th>
                  <th className="text-left p-4 text-[10px] uppercase text-zinc-500 font-bold">Elite Salary</th>
                  <th className="text-left p-4 text-[10px] uppercase text-zinc-500 font-bold">Progress</th>
                  <th className="text-left p-4 text-[10px] uppercase text-zinc-500 font-bold">Status</th>
                </tr></thead>
                <tbody>
                  {data.per_account.map((a) => (
                    <tr key={a.account_id} className="border-b border-white/5 last:border-b-0" data-testid={`elite-row-${a.account_number}`}>
                      <td className="p-4 text-white font-mono">{a.account_number}</td>
                      <td className="p-4 text-zinc-300">{a.plan_name || '—'}</td>
                      <td className="p-4 text-brand-gold font-bold font-mono">${a.elite_salary_potential.toLocaleString()}/mo</td>
                      <td className="p-4">
                        <div className="flex items-center gap-3 min-w-[160px]">
                          <div className="flex-1 h-1.5 bg-black/40 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full ${a.is_elite ? 'bg-gradient-to-r from-brand-gold to-brand-gold-soft' : 'bg-brand-gold/60'}`} style={{ width: `${a.progress_pct}%` }} />
                          </div>
                          <span className="text-xs text-zinc-400 font-mono whitespace-nowrap">{a.payouts_completed}/{a.payouts_required}</span>
                        </div>
                      </td>
                      <td className="p-4">
                        {a.is_elite ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider bg-brand-gold/15 text-brand-gold border border-brand-gold/30 rounded-full">
                            <Crown className="w-2.5 h-2.5" /> Elite
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-white/5 text-zinc-400 rounded-full">
                            <Lock className="w-2.5 h-2.5" /> Locked
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};




const KYCView = () => {
  const [kyc, setKyc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ full_name: '', aadhar_number: '', pan_number: '' });
  const [aadharFile, setAadharFile] = useState(null);
  const [panFile, setPanFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const aadharRef = useRef(null);
  const panRef = useRef(null);

  const fetchKyc = async () => {
    try { const { data } = await api.get('/kyc/me'); setKyc(data); } catch (_e) { setKyc({ status: 'not_submitted' }); } finally { setLoading(false); }
  };
  useEffect(() => { fetchKyc(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim()) { toast.error('Full name required'); return; }
    if (!/^\d{12}$/.test(form.aadhar_number.replace(/\D/g, ''))) { toast.error('Aadhaar must be 12 digits'); return; }
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/i.test(form.pan_number.trim())) { toast.error('PAN format: ABCDE1234F'); return; }
    if (!aadharFile) { toast.error('Upload Aadhaar image'); return; }
    if (!panFile) { toast.error('Upload PAN image'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('full_name', form.full_name.trim());
      fd.append('aadhar_number', form.aadhar_number.replace(/\D/g, ''));
      fd.append('pan_number', form.pan_number.toUpperCase().trim());
      fd.append('aadhar_image', aadharFile);
      fd.append('pan_image', panFile);
      await api.post('/kyc/submit', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('KYC submitted! We will review within 24 hours.');
      setAadharFile(null); setPanFile(null);
      fetchKyc();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-brand-primary border-t-transparent rounded-full animate-spin" /></div>;

  const status = kyc?.status || 'not_submitted';
  const showForm = status === 'not_submitted' || status === 'rejected';

  return (
    <div data-testid="kyc-view">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <h2 className="text-2xl md:text-3xl font-black text-white flex items-center gap-2.5">
            <ShieldCheck className="w-7 h-7 text-brand-primary" /> KYC Verification
          </h2>
          <p className="text-zinc-500 text-sm mt-1">Verify your identity to enable payouts. Indian Aadhaar + PAN required for compliance.</p>
        </div>
        {/* Status badge */}
        {status === 'pending' && <span className="px-3 py-1.5 rounded-full bg-yellow-400/15 text-yellow-400 border border-yellow-400/30 text-[11px] font-black uppercase tracking-wider flex items-center gap-1.5" data-testid="kyc-status-pending"><Clock className="w-3 h-3" /> Under Review</span>}
        {status === 'approved' && <span className="px-3 py-1.5 rounded-full bg-brand-positive/15 text-brand-positive border border-brand-positive/30 text-[11px] font-black uppercase tracking-wider flex items-center gap-1.5" data-testid="kyc-status-approved"><CheckCircle2 className="w-3 h-3" /> Verified</span>}
        {status === 'rejected' && <span className="px-3 py-1.5 rounded-full bg-brand-negative/15 text-brand-negative border border-brand-negative/30 text-[11px] font-black uppercase tracking-wider flex items-center gap-1.5" data-testid="kyc-status-rejected"><AlertCircle className="w-3 h-3" /> Rejected</span>}
      </div>

      {status === 'pending' && (
        <div className="bg-yellow-400/[0.04] border border-yellow-400/15 rounded-2xl p-6">
          <h3 className="text-white font-bold text-lg flex items-center gap-2"><Clock className="w-5 h-5 text-yellow-400" /> Under Review</h3>
          <p className="text-zinc-400 text-sm mt-2 leading-relaxed">Your KYC is being verified by our compliance team. We typically complete reviews within 24 hours. You will receive an email once your verification is complete.</p>
          <div className="mt-4 grid grid-cols-2 gap-3 max-w-md">
            <div className="p-3 rounded-lg bg-black/40 border border-white/5"><div className="text-[10px] uppercase text-zinc-500 font-bold">Name</div><div className="text-white text-sm">{kyc.full_name}</div></div>
            <div className="p-3 rounded-lg bg-black/40 border border-white/5"><div className="text-[10px] uppercase text-zinc-500 font-bold">Submitted</div><div className="text-white text-sm">{new Date(kyc.submitted_at).toLocaleDateString()}</div></div>
            <div className="p-3 rounded-lg bg-black/40 border border-white/5"><div className="text-[10px] uppercase text-zinc-500 font-bold">Aadhaar</div><div className="text-white text-sm font-mono">{kyc.aadhar_masked}</div></div>
            <div className="p-3 rounded-lg bg-black/40 border border-white/5"><div className="text-[10px] uppercase text-zinc-500 font-bold">PAN</div><div className="text-white text-sm font-mono">{kyc.pan_masked}</div></div>
          </div>
        </div>
      )}

      {status === 'approved' && (
        <div className="bg-brand-positive/[0.04] border border-brand-positive/15 rounded-2xl p-6">
          <h3 className="text-white font-bold text-lg flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-brand-positive" /> Identity Verified</h3>
          <p className="text-zinc-400 text-sm mt-2">Your KYC is approved. You can now request payouts without restrictions.</p>
          <div className="mt-4 grid grid-cols-2 gap-3 max-w-md">
            <div className="p-3 rounded-lg bg-black/40 border border-white/5"><div className="text-[10px] uppercase text-zinc-500 font-bold">Name</div><div className="text-white text-sm">{kyc.full_name}</div></div>
            <div className="p-3 rounded-lg bg-black/40 border border-white/5"><div className="text-[10px] uppercase text-zinc-500 font-bold">Verified On</div><div className="text-white text-sm">{kyc.reviewed_at ? new Date(kyc.reviewed_at).toLocaleDateString() : '—'}</div></div>
          </div>
        </div>
      )}

      {status === 'rejected' && (
        <div className="bg-brand-negative/[0.04] border border-brand-negative/15 rounded-2xl p-5 mb-6">
          <h3 className="text-white font-bold flex items-center gap-2"><AlertCircle className="w-4 h-4 text-brand-negative" /> KYC Rejected</h3>
          <p className="text-zinc-300 text-sm mt-2"><strong>Reason:</strong> {kyc.admin_note || 'Documents could not be verified.'}</p>
          <p className="text-zinc-500 text-xs mt-2">Please re-submit with clearer documents below.</p>
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-[#0F1420] border border-white/8 rounded-2xl p-6 md:p-8 space-y-5" data-testid="kyc-form">
          {/* Trust strip */}
          <div className="flex items-center justify-between gap-2 overflow-x-auto no-scrollbar pb-2 border-b border-white/5">
            {[
              { i: Shield, t: 'Bank-grade encryption' },
              { i: Lock, t: 'Indian DPDP compliant' },
              { i: CheckCircle2, t: 'Reviewed within 24h' },
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-1.5 text-[10px] font-bold text-zinc-400 whitespace-nowrap">
                <b.i className="w-3 h-3 text-brand-positive" /> <span className="uppercase tracking-wider">{b.t}</span>
              </div>
            ))}
          </div>

          <div>
            <label className="block text-[10px] uppercase text-zinc-400 font-black tracking-wider mb-1">Full Name (as per PAN) *</label>
            <input type="text" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})}
              className="w-full px-3 py-2.5 bg-[#050505] border border-white/10 rounded-lg text-white text-sm focus:border-brand-primary outline-none"
              placeholder="Rajesh Kumar Sharma" data-testid="kyc-name" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] uppercase text-zinc-400 font-black tracking-wider mb-1">Aadhaar Number *</label>
              <input type="text" value={form.aadhar_number} onChange={e => setForm({...form, aadhar_number: e.target.value})}
                maxLength={14} placeholder="1234 5678 9012"
                className="w-full px-3 py-2.5 bg-[#050505] border border-white/10 rounded-lg text-white text-sm font-mono focus:border-brand-primary outline-none"
                data-testid="kyc-aadhar" />
              <div className="text-[10px] text-zinc-600 mt-1">12-digit Aadhaar number</div>
            </div>
            <div>
              <label className="block text-[10px] uppercase text-zinc-400 font-black tracking-wider mb-1">PAN Number *</label>
              <input type="text" value={form.pan_number} onChange={e => setForm({...form, pan_number: e.target.value.toUpperCase()})}
                maxLength={10} placeholder="ABCDE1234F"
                className="w-full px-3 py-2.5 bg-[#050505] border border-white/10 rounded-lg text-white text-sm font-mono uppercase focus:border-brand-primary outline-none"
                data-testid="kyc-pan" />
              <div className="text-[10px] text-zinc-600 mt-1">Format: ABCDE1234F</div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] uppercase text-zinc-400 font-black tracking-wider mb-1">Aadhaar Image * <span className="text-zinc-600 normal-case font-normal">(max 5MB)</span></label>
              <input ref={aadharRef} type="file" accept="image/*" onChange={e => setAadharFile(e.target.files?.[0] || null)} className="hidden" data-testid="kyc-aadhar-file" />
              <button type="button" onClick={() => aadharRef.current?.click()}
                className={`w-full flex items-center justify-center gap-2 px-3 py-3 rounded-lg transition-all ${aadharFile ? 'bg-brand-positive/10 border border-brand-positive/30 text-brand-positive' : 'bg-[#050505] border border-dashed border-white/20 text-zinc-300 hover:border-brand-primary'}`}>
                {aadharFile ? <CheckCircle2 className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                <span className="text-sm truncate max-w-[200px]">{aadharFile?.name || 'Upload Aadhaar'}</span>
              </button>
            </div>
            <div>
              <label className="block text-[10px] uppercase text-zinc-400 font-black tracking-wider mb-1">PAN Image * <span className="text-zinc-600 normal-case font-normal">(max 5MB)</span></label>
              <input ref={panRef} type="file" accept="image/*" onChange={e => setPanFile(e.target.files?.[0] || null)} className="hidden" data-testid="kyc-pan-file" />
              <button type="button" onClick={() => panRef.current?.click()}
                className={`w-full flex items-center justify-center gap-2 px-3 py-3 rounded-lg transition-all ${panFile ? 'bg-brand-positive/10 border border-brand-positive/30 text-brand-positive' : 'bg-[#050505] border border-dashed border-white/20 text-zinc-300 hover:border-brand-primary'}`}>
                {panFile ? <CheckCircle2 className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                <span className="text-sm truncate max-w-[200px]">{panFile?.name || 'Upload PAN'}</span>
              </button>
            </div>
          </div>
          <button type="submit" disabled={submitting}
            className="w-full py-3.5 bg-brand-primary text-black font-black rounded-xl hover:shadow-[0_0_30px_rgba(0,212,255,0.4)] transition-all disabled:opacity-40 flex items-center justify-center gap-2"
            data-testid="kyc-submit">
            {submitting ? <><div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> Submitting...</> : <><Shield className="w-4 h-4" /> Submit for Verification</>}
          </button>
          <p className="text-[10px] text-zinc-600 text-center leading-relaxed">
            By submitting, you authorize AYRONN Funding to verify your identity for AML / KYC compliance under Indian regulations. Your data is encrypted and never shared with third parties.
          </p>
        </form>
      )}
    </div>
  );
};

const PayoutsView = ({ accounts }) => {
  const [payouts, setPayouts] = useState([]); const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ account_id: '', amount: '', crypto_address: '' });
  useEffect(() => { api.get('/payouts').then(r => setPayouts(r.data)).finally(() => setLoading(false)); }, []);
  const handleRequest = async () => {
    if(!form.account_id||!form.amount){toast.error('Select account and enter amount');return;}
    if(!form.crypto_address||form.crypto_address.trim().length<10){toast.error('Enter a valid TRC20 wallet address');return;}
    try{await api.post('/payouts/request',{account_id:form.account_id,amount:parseFloat(form.amount),crypto_address:form.crypto_address.trim(),network:'TRC20'});toast.success('Payout request submitted!');setForm({account_id:'',amount:'',crypto_address:''});const r=await api.get('/payouts');setPayouts(r.data);}catch(e){toast.error(e.response?.data?.detail||'Failed');}
  };
  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-2">Payouts</h2>
      <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-brand-primary/10 border border-brand-primary/30 rounded-full mb-6"><Clock className="w-4 h-4 text-brand-primary" /><span className="text-brand-primary text-sm font-semibold">Daily Payouts Available</span></div>
      <div className="bg-[#0F1420] border border-white/5 rounded-2xl p-6 mb-8">
        <h3 className="text-lg font-semibold text-white mb-4">Request Payout</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <select value={form.account_id} onChange={e=>setForm({...form,account_id:e.target.value})} className="px-4 py-2.5 bg-[#050505] border border-white/10 rounded-lg text-white text-sm" data-testid="payout-account-select"><option value="">Select account</option>{accounts.filter(a=>a.status==='active'||a.status==='passed').map(a=><option key={a.id} value={a.id}>{a.account_number} - ${Number(a.balance).toLocaleString()}</option>)}</select>
          <input type="number" placeholder="Amount ($)" value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})} className="px-4 py-2.5 bg-[#050505] border border-white/10 rounded-lg text-white text-sm" data-testid="payout-amount-input"/>
        </div>
        <div className="mb-4">
          <label className="block text-sm text-zinc-400 mb-1.5">TRC20 Wallet Address <span className="text-brand-negative">*</span></label>
          <input type="text" placeholder="Enter your TRC20 (USDT) wallet address" value={form.crypto_address} onChange={e=>setForm({...form,crypto_address:e.target.value})} className="w-full px-4 py-2.5 bg-[#050505] border border-white/10 rounded-lg text-white text-sm font-mono focus:border-brand-primary outline-none" data-testid="payout-crypto-address"/>
          <p className="text-xs text-zinc-500 mt-1">Payouts will be sent to this TRC20 address. Make sure it is correct.</p>
        </div>
        <button onClick={handleRequest} className="px-6 py-2.5 bg-brand-primary text-black font-semibold rounded-lg hover:glow-primary transition-all text-sm" data-testid="request-payout-btn">Request Payout</button>
      </div>
      <div className="bg-[#0F1420] border border-white/5 rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-white/5"><h3 className="text-lg font-semibold text-white">Payout History</h3></div>
        {payouts.length===0?<div className="p-8 text-center text-zinc-500">No payouts yet</div>:
        <div className="overflow-x-auto"><table className="w-full"><thead><tr className="border-b border-white/5"><th className="text-left p-4 text-xs uppercase text-zinc-500">Account</th><th className="text-left p-4 text-xs uppercase text-zinc-500">Amount</th><th className="text-left p-4 text-xs uppercase text-zinc-500">TRC20 Address</th><th className="text-left p-4 text-xs uppercase text-zinc-500">Status</th><th className="text-left p-4 text-xs uppercase text-zinc-500">Admin Note</th><th className="text-left p-4 text-xs uppercase text-zinc-500">Date</th></tr></thead>
        <tbody>{payouts.map((p,i)=><tr key={p.id} className="border-b border-white/5" data-testid={`user-payout-row-${i}`}><td className="p-4 text-sm text-white font-mono">{p.account_number}</td><td className="p-4 text-sm text-brand-primary font-bold font-mono">${p.amount}</td><td className="p-4 text-xs text-zinc-400 font-mono max-w-[200px] truncate">{p.crypto_address||'-'}</td><td className="p-4"><span className={`px-2 py-1 rounded text-xs font-bold uppercase ${p.status==='approved'?'bg-brand-positive/20 text-brand-positive':p.status==='pending'?'bg-yellow-400/20 text-yellow-400':'bg-brand-negative/20 text-brand-negative'}`}>{p.status}</span></td><td className="p-4 text-xs text-zinc-300 max-w-[240px] whitespace-normal" data-testid={`user-payout-note-${i}`}>{p.admin_note ? <span className={p.status==='rejected'?'text-red-300':'text-zinc-300'}>{p.admin_note}</span> : <span className="text-zinc-700">—</span>}</td><td className="p-4 text-sm text-zinc-400">{new Date(p.created_at).toLocaleDateString()}</td></tr>)}</tbody></table></div>}
      </div>
    </div>
  );
};

const ContractsView = () => {
  const [contracts, setContracts] = useState([]); const [signing, setSigning] = useState(null); const [sigName, setSigName] = useState('');
  useEffect(() => { api.get('/contracts').then(r => setContracts(r.data)).catch(() => {}); }, []);
  const AGREEMENTS = [
    { type: 'trading_rules', title: 'Trading Rules Agreement', desc: 'I acknowledge and agree to comply with all AYRONN Funding trading rules, including profit targets, drawdown limits, and daily loss limits.' },
    { type: 'risk_disclosure', title: 'Risk Disclosure Agreement', desc: 'I understand that trading involves substantial risk of loss and is not suitable for all investors. I accept all risks associated with funded trading.' },
    { type: 'payout_terms', title: 'Payout Terms Agreement', desc: 'I agree to the payout schedule (daily payouts available), profit split terms, and understand that payouts require admin approval.' },
    { type: 'platform_tos', title: 'Platform Terms of Service', desc: 'I agree to the AYRONN Funding platform terms of service, including account usage policies, data handling, and dispute resolution.' },
  ];
  const handleSign = async (type) => {
    if(!sigName.trim()){toast.error('Enter your full name as signature');return;}
    try{await api.post('/contracts/sign',{contract_type:type,signature_name:sigName,agreed:true});toast.success('Agreement signed!');setSigning(null);setSigName('');const r=await api.get('/contracts');setContracts(r.data);}catch(e){toast.error('Failed to sign');}
  };
  const isSigned = (type) => contracts.some(c => c.contract_type === type);
  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-2">Contracts & Agreements</h2>
      <p className="text-zinc-400 text-sm mb-6">Review and sign required agreements to maintain your funded account status.</p>
      <div className="space-y-4">
        {AGREEMENTS.map(ag => {
          const signed = isSigned(ag.type);
          const signedContract = contracts.find(c => c.contract_type === ag.type);
          return (
            <div key={ag.type} className={`bg-[#0F1420] border rounded-2xl p-6 ${signed ? 'border-brand-positive/30' : 'border-white/5'}`} data-testid={`contract-${ag.type}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">{signed && <CheckCircle2 className="w-5 h-5 text-brand-positive" />}{ag.title}</h3>
                  <p className="text-sm text-zinc-400 mt-1">{ag.desc}</p>
                </div>
              </div>
              {signed ? (
                <div className="mt-3 p-3 bg-brand-positive/5 border border-brand-positive/20 rounded-xl"><p className="text-sm text-brand-positive">Signed by <span className="font-bold">{signedContract.signature_name}</span> on {new Date(signedContract.signed_at).toLocaleDateString()}</p></div>
              ) : signing === ag.type ? (
                <div className="mt-4 space-y-3">
                  <div className="p-4 bg-yellow-500/5 border border-yellow-500/20 rounded-xl"><p className="text-yellow-400 text-sm">By typing your name below, you agree to the terms outlined in this agreement. This serves as your legally binding electronic signature.</p></div>
                  <div className="flex items-center gap-3">
                    <div className="relative flex-1"><PenTool className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" /><input type="text" value={sigName} onChange={e => setSigName(e.target.value)} placeholder="Type your full legal name" className="w-full pl-10 pr-4 py-3 bg-[#050505] border border-white/10 rounded-lg text-white focus:border-brand-primary outline-none font-mono italic" data-testid={`signature-input-${ag.type}`} /></div>
                    <button onClick={() => handleSign(ag.type)} className="px-6 py-3 bg-brand-primary text-black font-bold rounded-lg hover:glow-primary transition-all" data-testid={`sign-btn-${ag.type}`}>Sign</button>
                    <button onClick={() => {setSigning(null);setSigName('');}} className="px-4 py-3 bg-white/5 text-zinc-400 rounded-lg hover:text-white transition-all">Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setSigning(ag.type)} className="mt-3 px-5 py-2.5 bg-white/5 border border-white/10 text-white rounded-lg hover:bg-brand-primary/10 hover:border-brand-primary/30 transition-all text-sm font-semibold" data-testid={`review-sign-${ag.type}`}>Review & Sign</button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SettingsView = () => {
  const [profile, setProfile] = useState(null); const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', country: '', address: '' });
  useEffect(() => { api.get('/profile').then(r => { setProfile(r.data); setForm({ name: r.data.name || '', phone: r.data.phone || '', country: r.data.country || '', address: r.data.address || '' }); }); }, []);
  const handleSave = async () => {
    setSaving(true);
    try{const r=await api.put('/profile',form);setProfile(r.data);toast.success('Profile updated!');}catch(e){toast.error('Failed');}finally{setSaving(false);}
  };
  if(!profile) return <div className="text-zinc-500">Loading...</div>;
  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-6">Profile Settings</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-[#0F1420] border border-white/5 rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Personal Information</h3>
          <div className="space-y-4">
            <div><label className="block text-sm text-zinc-400 mb-1.5">Full Name</label><input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} className="w-full px-4 py-2.5 bg-[#050505] border border-white/10 rounded-lg text-white focus:border-brand-primary outline-none" data-testid="profile-name"/></div>
            <div><label className="block text-sm text-zinc-400 mb-1.5">Email</label><input value={profile.email} disabled className="w-full px-4 py-2.5 bg-[#050505] border border-white/10 rounded-lg text-zinc-500 cursor-not-allowed"/></div>
            <div><label className="block text-sm text-zinc-400 mb-1.5">Phone</label><input value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} placeholder="+1 234 567 8900" className="w-full px-4 py-2.5 bg-[#050505] border border-white/10 rounded-lg text-white focus:border-brand-primary outline-none" data-testid="profile-phone"/></div>
            <div><label className="block text-sm text-zinc-400 mb-1.5">Country</label><input value={form.country} onChange={e=>setForm({...form,country:e.target.value})} placeholder="United States" className="w-full px-4 py-2.5 bg-[#050505] border border-white/10 rounded-lg text-white focus:border-brand-primary outline-none" data-testid="profile-country"/></div>
            <div><label className="block text-sm text-zinc-400 mb-1.5">Address</label><input value={form.address} onChange={e=>setForm({...form,address:e.target.value})} placeholder="123 Main St" className="w-full px-4 py-2.5 bg-[#050505] border border-white/10 rounded-lg text-white focus:border-brand-primary outline-none" data-testid="profile-address"/></div>
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-6 py-2.5 bg-brand-primary text-black font-semibold rounded-lg hover:glow-primary transition-all disabled:opacity-50" data-testid="save-profile-btn"><Save className="w-4 h-4"/>{saving?'Saving...':'Save Changes'}</button>
          </div>
        </div>
        <div className="space-y-6">
          <div className="bg-[#0F1420] border border-white/5 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Account Info</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-zinc-400">Member Since</span><span className="text-white">{new Date(profile.created_at).toLocaleDateString()}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Role</span><span className="text-white capitalize">{profile.role}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">User ID</span><span className="text-zinc-500 font-mono text-xs">{profile._id}</span></div>
            </div>
          </div>
          <div className="bg-[#0F1420] border border-brand-primary/20 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-2">Daily Payouts</h3>
            <p className="text-sm text-zinc-400">AYRONN Funding offers <span className="text-brand-primary font-bold">daily payouts</span> — our industry-leading USP. Request your profits anytime and get approved within 24 hours.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const AffiliateView = () => {
  const [data, setData] = useState(null); const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [payoutAmt, setPayoutAmt] = useState('');
  useEffect(() => {
    api.get('/affiliate/dashboard')
      .then(r => setData(r.data))
      .catch(e => { if (e.response?.status === 403) setAccessDenied(true); })
      .finally(() => setLoading(false));
  }, []);
  const copyLink = () => { if (data?.referral_link) navigator.clipboard.writeText(data.referral_link).then(() => toast.success('Referral link copied!')); };
  const copyCode = () => { if (data?.promo_code) navigator.clipboard.writeText(data.promo_code).then(() => toast.success('Promo code copied!')); };
  const requestPayout = async () => {
    if (!payoutAmt || parseFloat(payoutAmt) <= 0) { toast.error('Enter valid amount'); return; }
    try { await api.post('/affiliate/request-payout', { amount: parseFloat(payoutAmt) }); toast.success('Payout requested!'); setPayoutAmt(''); const r = await api.get('/affiliate/dashboard'); setData(r.data); } catch (e) { toast.error('Failed'); }
  };
  if (loading) return <div className="grid grid-cols-1 md:grid-cols-3 gap-6">{[...Array(3)].map((_,i) => <div key={i} className="h-32 skeleton rounded-2xl" />)}</div>;
  if (accessDenied || !data) {
    return (
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Affiliate Program</h2>
        <div className="bg-[#0F1420] border border-white/5 rounded-2xl p-12 text-center" data-testid="affiliate-access-denied">
          <Gift className="w-14 h-14 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-3">Affiliate Program is Private</h3>
          <p className="text-zinc-500 mb-2">Our affiliate program is by invitation only.</p>
          <p className="text-zinc-500 text-sm">If you're interested in joining, contact us at <a href="mailto:ayronnsupport@gmail.com" className="text-brand-primary hover:underline">ayronnsupport@gmail.com</a></p>
        </div>
      </div>
    );
  }
  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-2">Affiliate Program</h2>
      <p className="text-zinc-400 text-sm mb-6">Earn {data.commission_pct}% commission on every sale via your promo code</p>

      {/* Promo Code Card */}
      <div className="bg-gradient-to-br from-[#7B61FF]/10 to-brand-primary/5 border border-brand-primary/30 rounded-2xl p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Your Promo Code</div>
            <div className="flex items-center gap-2">
              <div className="flex-1 px-4 py-3 bg-[#050505] border border-brand-primary/40 rounded-lg text-brand-primary font-mono text-xl font-black tracking-widest" data-testid="affiliate-promo-code">{data.promo_code}</div>
              <button onClick={copyCode} className="px-4 py-3 bg-brand-primary text-black font-semibold rounded-lg" data-testid="copy-promo-code"><Copy className="w-4 h-4" /></button>
            </div>
            <div className="text-xs text-zinc-500 mt-2">Customers get <span className="text-brand-positive font-bold">{data.discount_pct}% OFF</span></div>
          </div>
          <div>
            <div className="text-xs text-zinc-400 uppercase tracking-wider mb-2">Referral Link</div>
            <div className="flex items-center gap-2">
              <input value={data.referral_link} readOnly className="flex-1 px-3 py-3 bg-[#050505] border border-white/10 rounded-lg text-brand-primary font-mono text-xs" data-testid="affiliate-referral-link" />
              <button onClick={copyLink} className="px-4 py-3 bg-white/5 border border-white/10 text-white rounded-lg"><Copy className="w-4 h-4" /></button>
            </div>
            <div className="text-xs text-zinc-500 mt-2">Your commission: <span className="text-brand-primary font-bold">{data.commission_pct}% per sale</span></div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          {l:'Total Sales',v:data.total_sales,c:'text-white'},
          {l:'Revenue Generated',v:`$${data.total_revenue}`,c:'text-brand-primary'},
          {l:'My Commission',v:`$${data.total_commission}`,c:'text-brand-positive'},
          {l:'Available',v:`$${data.available}`,c:'text-brand-positive'},
        ].map((s,i) => (
          <div key={i} className="p-5 bg-[#0F1420] border border-white/5 rounded-xl" data-testid={`affiliate-stat-${i}`}><div className="text-xs text-zinc-500 uppercase mb-1">{s.l}</div><div className={`text-2xl font-black font-mono ${s.c}`}>{s.v}</div></div>
        ))}
      </div>

      {/* Recent Sales */}
      {data.recent_sales && data.recent_sales.length > 0 && (
        <div className="bg-[#0F1420] border border-white/5 rounded-2xl overflow-hidden mb-6">
          <div className="p-4 border-b border-white/5"><h3 className="text-lg font-semibold text-white">Recent Sales (Auto-Tracked)</h3></div>
          <div className="overflow-x-auto"><table className="w-full">
            <thead><tr className="border-b border-white/5"><th className="text-left p-4 text-xs uppercase text-zinc-500">Date</th><th className="text-left p-4 text-xs uppercase text-zinc-500">Sale Amount</th><th className="text-left p-4 text-xs uppercase text-zinc-500">Commission</th></tr></thead>
            <tbody>{data.recent_sales.map((s,i) => <tr key={i} className="border-b border-white/5"><td className="p-4 text-sm text-zinc-400">{new Date(s.created_at).toLocaleDateString()}</td><td className="p-4 text-sm text-white font-mono">${s.sale_amount}</td><td className="p-4 text-brand-positive font-bold font-mono">${s.commission_amount}</td></tr>)}</tbody>
          </table></div>
        </div>
      )}

      {/* Payout Request */}
      {data.available > 0 && (
        <div className="bg-[#0F1420] border border-white/5 rounded-2xl p-6 mb-6">
          <h3 className="text-lg font-semibold text-white mb-3">Request Affiliate Payout</h3>
          <div className="flex gap-3"><input type="number" placeholder="Amount ($)" value={payoutAmt} onChange={e => setPayoutAmt(e.target.value)} className="flex-1 px-4 py-2.5 bg-[#050505] border border-white/10 rounded-lg text-white text-sm" data-testid="affiliate-payout-input" />
          <button onClick={requestPayout} className="px-6 py-2.5 bg-brand-primary text-black font-semibold rounded-lg text-sm" data-testid="affiliate-payout-btn">Request</button></div>
        </div>
      )}

      {/* Payout History */}
      {data.payouts.length > 0 && (
        <div className="bg-[#0F1420] border border-white/5 rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-white/5"><h3 className="text-lg font-semibold text-white">Payout History</h3></div>
          <table className="w-full"><thead><tr className="border-b border-white/5"><th className="text-left p-4 text-xs uppercase text-zinc-500">Amount</th><th className="text-left p-4 text-xs uppercase text-zinc-500">Status</th><th className="text-left p-4 text-xs uppercase text-zinc-500">Date</th></tr></thead>
          <tbody>{data.payouts.map((p,i) => <tr key={i} className="border-b border-white/5"><td className="p-4 text-brand-primary font-bold font-mono">${p.amount}</td><td className="p-4"><span className={`px-2 py-1 rounded text-xs font-bold uppercase ${p.status==='approved'?'bg-brand-positive/20 text-brand-positive':p.status==='pending'?'bg-yellow-400/20 text-yellow-400':'bg-brand-negative/20 text-brand-negative'}`}>{p.status}</span></td><td className="p-4 text-sm text-zinc-400">{new Date(p.created_at).toLocaleDateString()}</td></tr>)}</tbody></table>
        </div>
      )}
    </div>
  );
};

const CertificatesView = ({ accounts }) => {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const passed = accounts.filter(a => a.status === 'passed' || a.status === 'funded');

  useEffect(() => {
    api.get('/certificates').then(r => setCerts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleDownload = async (cert) => {
    try {
      const { data } = await api.get(`/certificates/${cert.id}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a'); a.href = url; a.download = cert.filename; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-6">Certificates & Achievements</h2>

      {/* Uploaded Certificates */}
      {loading ? <div className="text-zinc-500 text-sm">Loading...</div> : certs.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {certs.map(c => (
            <div key={c.id} className="bg-[#0F1420] border border-brand-primary/20 rounded-2xl p-6 flex items-center gap-4" data-testid={`cert-${c.id}`}>
              <div className="w-12 h-12 bg-brand-primary/10 rounded-xl flex items-center justify-center flex-shrink-0"><Award className="w-6 h-6 text-brand-primary" /></div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-bold truncate">{c.title}</div>
                <div className="text-xs text-zinc-500">{c.filename} &middot; {new Date(c.created_at).toLocaleDateString()}</div>
              </div>
              <button onClick={() => handleDownload(c)} className="px-3 py-1.5 bg-brand-primary/10 text-brand-primary text-xs font-bold rounded-lg hover:bg-brand-primary/20 transition-all flex-shrink-0" data-testid={`download-cert-${c.id}`}>
                <Download className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Auto-generated from passed accounts */}
      {passed.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-zinc-400">Challenge Achievements</h3>
          {passed.map(acc => (
            <div key={acc.id} className="bg-[#0F1420] border border-white/5 rounded-2xl p-6">
              <div className="flex items-center gap-4"><Award className="w-8 h-8 text-brand-positive" /><div><div className="text-white font-bold">{acc.status === 'funded' ? 'Funded Trader' : 'Challenge Passed'}</div><div className="text-sm text-zinc-400">{acc.plan_name} - {acc.account_number}</div></div></div>
              <div className="text-xs text-zinc-500 mt-2">Account Size: ${Number(acc.account_size).toLocaleString()} | Profit Split: {acc.profit_split}%</div>
            </div>
          ))}
        </div>
      )}

      {certs.length === 0 && passed.length === 0 && (
        <div className="bg-[#0F1420] border border-white/5 rounded-2xl p-12 text-center">
          <Award className="w-14 h-14 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-white mb-3">No Certificates Available</h3>
          <p className="text-zinc-500">Pass a trading challenge to earn your certificate.</p>
        </div>
      )}
    </div>
  );
};

const CalendarView = () => (
  <div>
    <h2 className="text-2xl font-bold text-white mb-6">Economic Calendar</h2>
    <div className="bg-[#0F1420] border border-white/5 rounded-2xl overflow-hidden" style={{height: '600px'}}>
      <iframe src="https://sslecal2.investing.com?columns=exc_flags,exc_currency,exc_importance,exc_actual,exc_forecast,exc_previous&importance=2,3&features=datepicker,timezone,filters&countries=25,32,6,37,72,22,17,39,14,10,35,43,56,36,110,11,26,12,4,5&calType=week&timeZone=55&lang=1" width="100%" height="100%" frameBorder="0" title="Economic Calendar" style={{border: 'none', background: '#0F1420'}} />
    </div>
  </div>
);

const HelpView = () => {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hi! I'm AYRONN Support Bot, powered by Claude Sonnet 4.5. Ask me anything about trading rules, payouts, challenges, or your account. How can I help?" }
  ]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const scrollRef = useRef(null);

  useEffect(() => {
    // Load last session history
    api.get('/support/history').then(r => {
      if (r.data?.messages?.length) {
        setMessages(r.data.messages);
        setSessionId(r.data.session_id);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setMessages(m => [...m, { role: 'user', content: text }]);
    setInput('');
    try {
      const { data } = await api.post('/support/chat', { message: text, session_id: sessionId || null });
      if (data.session_id && !sessionId) setSessionId(data.session_id);
      setMessages(m => [...m, { role: 'assistant', content: data.reply }]);
    } catch (e) {
      const errMsg = e?.response?.data?.detail || 'Something went wrong. Please try again.';
      setMessages(m => [...m, { role: 'assistant', content: `⚠️ ${errMsg}`, error: true }]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const newChat = () => {
    setMessages([{ role: 'assistant', content: "New chat started. How can I help you with AYRONN Funding?" }]);
    setSessionId('');
  };

  const suggested = [
    'What are the rules for 2-Step Challenge?',
    'How do I request a payout?',
    'What is the consistency rule?',
    'What happens if my account is breached?',
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-white">Help Center</h2>
          <p className="text-zinc-500 text-sm mt-1">AI-powered support · Instant answers 24/7</p>
        </div>
        <button onClick={newChat} className="text-xs px-3 py-1.5 bg-white/5 border border-white/10 text-zinc-300 rounded-lg hover:bg-white/10" data-testid="new-chat-btn">+ New Chat</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chat Panel */}
        <div className="lg:col-span-2 bg-[#0F1420] border border-white/5 rounded-2xl overflow-hidden flex flex-col" style={{ height: '560px' }} data-testid="support-chat-panel">
          {/* Header */}
          <div className="px-5 py-3 border-b border-white/5 flex items-center gap-3 bg-gradient-to-r from-brand-primary/5 to-transparent">
            <div className="w-9 h-9 rounded-full bg-brand-primary/15 border border-brand-primary/30 flex items-center justify-center">
              <HelpCircle className="w-5 h-5 text-brand-primary" />
            </div>
            <div>
              <div className="text-white text-sm font-semibold">AYRONN Support Bot</div>
              <div className="text-[10px] text-brand-positive flex items-center gap-1"><span className="w-1.5 h-1.5 bg-brand-positive rounded-full animate-pulse" />Online · Claude Sonnet 4.5</div>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="support-chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-brand-primary text-black font-medium rounded-br-sm' : m.error ? 'bg-red-500/10 border border-red-500/30 text-red-300 rounded-bl-sm' : 'bg-white/5 border border-white/5 text-zinc-200 rounded-bl-sm'}`}>
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="px-4 py-3 bg-white/5 border border-white/5 rounded-2xl rounded-bl-sm flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-brand-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-brand-primary rounded-full animate-bounce" style={{ animationDelay: '120ms' }} />
                  <span className="w-2 h-2 bg-brand-primary rounded-full animate-bounce" style={{ animationDelay: '240ms' }} />
                </div>
              </div>
            )}
          </div>

          {/* Suggestions (only when just the greeting exists) */}
          {messages.length <= 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {suggested.map((s, i) => (
                <button key={i} onClick={() => setInput(s)} className="text-xs px-3 py-1.5 bg-white/5 border border-white/10 text-zinc-300 rounded-full hover:bg-brand-primary/10 hover:border-brand-primary/30 hover:text-white transition-all" data-testid={`suggest-${i}`}>{s}</button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-white/5 flex gap-2">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Ask about rules, payouts, accounts..."
              disabled={sending}
              className="flex-1 px-4 py-2.5 bg-[#050505] border border-white/10 rounded-lg text-white text-sm focus:border-brand-primary focus:ring-1 focus:ring-brand-primary/40 outline-none disabled:opacity-50"
              data-testid="support-chat-input"
            />
            <button onClick={send} disabled={sending || !input.trim()} className="px-5 py-2.5 bg-brand-primary text-black text-sm font-semibold rounded-lg hover:glow-primary transition-all disabled:opacity-40 disabled:cursor-not-allowed" data-testid="support-chat-send">Send</button>
          </div>
        </div>

        {/* Side Panel */}
        <div className="space-y-6">
          <div className="bg-[#0F1420] border border-white/5 rounded-2xl p-6">
            <HelpCircle className="w-8 h-8 text-brand-primary mb-3" />
            <h3 className="text-base font-bold text-white mb-2">Need a human?</h3>
            <p className="text-zinc-400 text-xs mb-4">Our team responds within 24 hours.</p>
            <a href="mailto:ayronnsupport@gmail.com" className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 text-white font-semibold rounded-lg hover:bg-brand-primary hover:text-black hover:border-brand-primary transition-all text-xs" data-testid="contact-support-btn">Email Support</a>
            <p className="text-zinc-500 text-[11px] mt-2 font-mono">ayronnsupport@gmail.com</p>
          </div>
          <div className="bg-[#0F1420] border border-white/5 rounded-2xl p-6">
            <FileText className="w-8 h-8 text-brand-primary mb-3" />
            <h3 className="text-base font-bold text-white mb-3">Quick FAQ</h3>
            <div className="space-y-3 text-xs">
              {[['How do payouts work?','Daily TRC20 USDT payouts. Request anytime.'],['What are trading rules?','Each challenge has profit targets + drawdown limits.'],['Breached account?','Contact support for review.']].map(([q,a],i)=>(
                <div key={i}><div className="text-white font-semibold">{q}</div><div className="text-zinc-400 mt-0.5">{a}</div></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DownloadsView = () => (
  <div>
    <h2 className="text-2xl font-bold text-white mb-6">Downloads</h2>
    <div className="bg-[#0F1420] border border-white/5 rounded-2xl p-12 text-center">
      <Download className="w-14 h-14 text-zinc-600 mx-auto mb-4" />
      <h3 className="text-xl font-bold text-white mb-3">No Downloads Available</h3>
      <p className="text-zinc-500">Downloadable resources will appear here when available.</p>
    </div>
  </div>
);

const SimplePage = ({ title, desc }) => (
  <div><h2 className="text-2xl font-bold text-white mb-4">{title}</h2><div className="bg-[#0F1420] border border-white/5 rounded-2xl p-12 text-center"><p className="text-zinc-500">{desc}</p></div></div>
);

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]); const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true); const [activeSection, setActiveSection] = useState('overview');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedMetrics, setSelectedMetrics] = useState(null);
  const [eliteData, setEliteData] = useState(null);
  const [eliteLoading, setEliteLoading] = useState(true);
  const eliteByAccount = useMemo(() => {
    const map = {};
    (eliteData?.per_account || []).forEach(a => { map[a.account_id] = a; });
    return map;
  }, [eliteData]);

  useEffect(() => {
    fetchData();
    const onResize = () => { if (window.innerWidth < 768) setSidebarCollapsed(true); };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Fire Meta Pixel Purchase event when user returns from OxaPay with ?payment=success
  useEffect(() => {
    if (searchParams.get('payment') === 'success') {
      try {
        const raw = sessionStorage.getItem('ayronn_pending_purchase');
        if (raw) {
          const pending = JSON.parse(raw);
          fbTrack('Purchase', {
            content_ids: [pending.plan_id],
            content_name: pending.name,
            content_type: 'product',
            value: pending.value,
            currency: 'USD',
          });
          sessionStorage.removeItem('ayronn_pending_purchase');
        } else {
          fbTrack('Purchase', { currency: 'USD' });
        }
      } catch (_e) { fbTrack('Purchase', { currency: 'USD' }); }
      toast.success('Payment successful! Your trading account is being prepared.');
      // Clean query params so the event fires only once
      searchParams.delete('payment'); searchParams.delete('order');
      setSearchParams(searchParams, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const fetchData = async () => {
    try { const [a, t] = await Promise.all([api.get('/accounts'), api.get('/transactions')]); setAccounts(a.data); setTransactions(t.data); } catch (e) {} finally { setLoading(false); }
    try { const r = await api.get('/elite/status'); setEliteData(r.data); } catch (e) {} finally { setEliteLoading(false); }
  };
  const handleLogout = async () => { await logout(); navigate('/'); };
  const copyCredentials = (acc) => {
    if(!acc.credentials_assigned){toast.info('Credentials will be assigned shortly');return;}
    navigator.clipboard.writeText(`Login: ${acc.login_id}\nPassword: ${acc.credentials_password}\nServer: ${acc.server}`).then(()=>toast.success('Credentials copied!')).catch(()=>toast.error('Failed'));
  };
  const handleSectionChange = (s) => { setActiveSection(s); setSelectedMetrics(null); setSidebarOpen(false); };

  const renderContent = () => {
    if(selectedMetrics) return <MetricsView account={selectedMetrics} onBack={()=>setSelectedMetrics(null)}/>;
    switch(activeSection){
      case 'overview': return (
        <div>
          <div className="flex items-center justify-between mb-8">
            <div><h2 className="text-2xl font-bold text-white">Accounts Overview</h2><p className="text-zinc-500 text-sm mt-1">Explore your trading accounts and start your journey!</p></div>
            <Link to="/pricing" className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-brand-primary text-black font-semibold rounded-xl hover:glow-primary transition-all text-sm" data-testid="header-start-challenge"><Plus className="w-4 h-4"/> Start New Challenge</Link>
          </div>
          {accounts.length===0?(
            <div className="bg-[#0F1420] border border-white/5 rounded-2xl p-12 text-center" data-testid="no-accounts-empty">
              <AlertCircle className="w-14 h-14 text-zinc-600 mx-auto mb-4"/><h3 className="text-xl font-bold text-white mb-3">No Trading Accounts</h3><p className="text-zinc-500 mb-6">Purchase your first challenge to start trading</p>
              <Link to="/pricing" className="inline-flex items-center gap-2 px-6 py-3 bg-brand-primary text-black font-semibold rounded-xl"><Plus className="w-5 h-5"/> Buy Challenge</Link>
            </div>
          ):(
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">{accounts.map(acc=><AccountCard key={acc.id} account={acc} eliteProgress={eliteByAccount[acc.id]} onMetrics={setSelectedMetrics} onCopyCredentials={copyCredentials}/>)}</div>
          )}
          {accounts.length>0&&<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            <div className="bg-[#0F1420] border border-white/5 rounded-2xl p-6"><div className="flex items-center gap-2 mb-3"><Clock className="w-5 h-5 text-brand-primary"/><h3 className="text-lg font-semibold text-white">Daily Payouts</h3></div><p className="text-sm text-zinc-400">Industry-leading daily payout schedule. Request your profits anytime!</p></div>
            <div className="bg-[#0F1420] border border-white/5 rounded-2xl p-6"><div className="flex items-center gap-2 mb-3"><HelpCircle className="w-5 h-5 text-brand-primary"/><h3 className="text-lg font-semibold text-white">FAQ</h3></div><p className="text-sm text-zinc-400">Have questions? Check our FAQ or reach out to support.</p></div>
          </div>}
        </div>
      );
      case 'payouts': return <PayoutsView accounts={accounts}/>;
      case 'elite-salary': return <EliteSalaryView eliteData={eliteData} eliteLoading={eliteLoading} />;
      case 'kyc': return <KYCView />;
      case 'billing': return (
        <div><h2 className="text-2xl font-bold text-white mb-6">Billing</h2>
          <div className="bg-[#0F1420] border border-white/5 rounded-2xl overflow-hidden">
            {transactions.length===0?<div className="p-8 text-center text-zinc-500">No transactions</div>:
            <table className="w-full"><thead><tr className="border-b border-white/5"><th className="text-left p-4 text-xs uppercase text-zinc-500">Date</th><th className="text-left p-4 text-xs uppercase text-zinc-500">Type</th><th className="text-left p-4 text-xs uppercase text-zinc-500">Description</th><th className="text-right p-4 text-xs uppercase text-zinc-500">Amount</th></tr></thead>
            <tbody>{transactions.map((t,i)=><tr key={t.id} className="border-b border-white/5"><td className="p-4 text-sm text-zinc-400">{new Date(t.created_at).toLocaleDateString()}</td><td className="p-4"><span className="px-2 py-1 bg-white/5 rounded text-xs text-white capitalize">{t.type}</span></td><td className="p-4 text-sm text-white">{t.description}</td><td className="p-4 text-right text-sm font-bold text-brand-primary font-mono">${t.amount}</td></tr>)}</tbody></table>}
          </div>
        </div>
      );
      case 'contracts': return <ContractsView/>;
      case 'affiliate': return <AffiliateView/>;
      case 'settings': return <SettingsView/>;
      case 'certificates': return <CertificatesView accounts={accounts} />;
      case 'calendar': return <CalendarView />;
      case 'help': return <HelpView />;
      case 'downloads': return <DownloadsView />;
      default: return <SimplePage title={SIDEBAR_ITEMS.find(s=>s.id===activeSection)?.label||'Page'} desc="This section is coming soon." />;
    }
  };

  if(loading) return <div className="min-h-screen bg-[#050505] flex"><div className="w-60 bg-[#0A0F1C] border-r border-white/5"/><div className="flex-1 p-8 pt-24"><div className="grid grid-cols-1 md:grid-cols-3 gap-6">{[...Array(3)].map((_,i)=><div key={i} className="h-[320px] skeleton rounded-2xl"/>)}</div></div></div>;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="user-dashboard">
      <Sidebar active={activeSection} setActive={handleSectionChange} collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed} onLogout={handleLogout} mobileOpen={sidebarOpen} setMobileOpen={setSidebarOpen} />
      <main className={`transition-all duration-300 min-h-screen ${sidebarCollapsed ? 'md:ml-16' : 'md:ml-60'}`}>
        <div className="h-14 border-b border-white/5 flex items-center justify-between px-4 md:px-8 bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="md:hidden text-zinc-400 hover:text-white" data-testid="mobile-sidebar-toggle"><BarChart3 className="w-5 h-5" /></button>
            <div className="text-sm text-zinc-400">Welcome, <span className="text-white font-semibold">{user?.name}</span></div>
          </div>
          <div className="flex items-center gap-3">
            {user?.email === 'ayushshahu91@gmail.com' && <Link to="/admin" className="text-xs text-brand-secondary hover:text-white transition-colors flex items-center gap-1" data-testid="admin-link"><ShieldCheck className="w-3 h-3"/> Admin</Link>}
            <Link to="/" className="text-xs text-zinc-500 hover:text-white transition-colors">AYRONN</Link>
          </div>
        </div>
        <div className="p-4 md:p-6 lg:p-8">
          <AnimatePresence mode="wait"><motion.div key={selectedMetrics?'m':activeSection} initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} exit={{opacity:0}} transition={{duration:0.2}}>{renderContent()}</motion.div></AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
